var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
/**
 * Compact, clickable status pill. Designed for status / filter rows
 * (e.g. "Draft 3", "Validated 12"). Colors follow the standard variant
 * palette used by `simple-button`. Emits `card-click` on activation.
 *
 *   <status-card label="Validated" .count=${5} variant="success" selected></status-card>
 */
let StatusCard = class StatusCard extends LitElement {
    constructor() {
        super(...arguments);
        this.label = '';
        this.count = null;
        this.variant = 'primary';
        this.selected = false;
        this.disabled = false;
    }
    _onClick(e) {
        if (this.disabled)
            return;
        e.preventDefault();
        e.stopPropagation();
        this.dispatchEvent(new CustomEvent('card-click', {
            detail: { label: this.label },
            bubbles: true,
            composed: true
        }));
    }
    render() {
        const variantClass = `chip--${this.variant}`;
        return html `
      <button
        class="chip ${variantClass}"
        type="button"
        ?disabled=${this.disabled}
        aria-pressed=${this.selected ? 'true' : 'false'}
        @click=${this._onClick}
      >
        <span>${this.label}</span>
        ${this.count != null
            ? html `<span class="count">${this.count}</span>`
            : ''}
      </button>
    `;
    }
};
StatusCard.styles = css `
    :host {
      display: inline-flex;
      font-family: inherit;
    }

    .chip {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 10px;
      border: 1px solid #e5e7eb;
      border-radius: 999px;
      background: #fff;
      font-size: 12px;
      font-weight: 600;
      color: #4b5563;
      cursor: pointer;
      transition: background 0.15s, border-color 0.15s, color 0.15s,
        box-shadow 0.15s;
    }

    .chip:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(0, 120, 212, 0.2);
    }

    .count {
      font-size: 11px;
      font-weight: 700;
      padding: 1px 6px;
      border-radius: 999px;
      background: #f3f4f6;
      color: #4b5563;
    }

    /* Variant outlines (unselected state). */
    .chip--primary {
      border-color: #2563eb;
      color: #1d4ed8;
    }
    .chip--primary .count {
      background: #eaf6ff;
      color: #1d4ed8;
    }

    .chip--secondary {
      border-color: #d1d5db;
      color: #4b5563;
    }
    .chip--secondary .count {
      background: #f3f4f6;
      color: #4b5563;
    }

    .chip--success {
      border-color: #28a745;
      color: #218838;
    }
    .chip--success .count {
      background: #e6f9ec;
      color: #218838;
    }

    .chip--danger {
      border-color: #dc3545;
      color: #c82333;
    }
    .chip--danger .count {
      background: #fbeaea;
      color: #c82333;
    }

    .chip--warning {
      border-color: #f59e0b;
      color: #b78400;
    }
    .chip--warning .count {
      background: #fffbe6;
      color: #b78400;
    }

    .chip:hover:not(:disabled) {
      filter: brightness(0.97);
    }

    /* Selected — solid variant fill. */
    :host([selected]) .chip--primary {
      background: #2563eb;
      border-color: #2563eb;
      color: #fff;
    }
    :host([selected]) .chip--secondary {
      background: #4b5563;
      border-color: #4b5563;
      color: #fff;
    }
    :host([selected]) .chip--success {
      background: #218838;
      border-color: #218838;
      color: #fff;
    }
    :host([selected]) .chip--danger {
      background: #c82333;
      border-color: #c82333;
      color: #fff;
    }
    :host([selected]) .chip--warning {
      background: #b78400;
      border-color: #b78400;
      color: #fff;
    }
    :host([selected]) .count {
      background: rgba(255, 255, 255, 0.25);
      color: #fff;
    }

    :host([disabled]) .chip {
      cursor: not-allowed;
      opacity: 0.5;
    }
  `;
__decorate([
    property({ type: String })
], StatusCard.prototype, "label", void 0);
__decorate([
    property({ type: Number })
], StatusCard.prototype, "count", void 0);
__decorate([
    property({ type: String })
], StatusCard.prototype, "variant", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], StatusCard.prototype, "selected", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], StatusCard.prototype, "disabled", void 0);
StatusCard = __decorate([
    customElement('status-card')
], StatusCard);
export { StatusCard };
//# sourceMappingURL=StatusCard.js.map